import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine
} from "recharts";
import ReactApexChart from "react-apexcharts";
import { Box, Typography } from "@mui/material";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

/* ---------------- AXIS FORMATTER ---------------- */

const formatAxis = (timestamp, period) => {
  const d = new Date(timestamp);

  if (period === "1D") {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

/* ---------------- TOOLTIP ---------------- */

const CustomTooltip = ({ active, payload, label, period, sym }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: "#020617",
        border: "1px solid #1e293b",
        color: "#e5e7eb",
        borderRadius: 2,
        px: 1.5,
        py: 1,
        fontSize: 12
      }}
    >
      <Typography fontSize="var(--font-size-xs2)" color="#94a3b8">
        {formatAxis(label, period)}
      </Typography>
      <Typography fontWeight={700}>{sym}{fmtL(d.close, 2)}</Typography>
    </Box>
  );
};

/* ---------------- COMPONENT ---------------- */

export default function ProStockChart({ data = [], chartType = "line", period }) {

  /* ALWAYS RUN HOOKS — NEVER RETURN BEFORE */
  const formatted = useMemo(() => {
    if (!data?.length) return [];

    return data.map((d) => ({
      ts: new Date(d.timestamp).getTime(),
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
      value: d.close,
      volume: d.volume || 0
    }));
  }, [data]);

  if (!formatted.length) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b"
        }}
      >
        No data available
      </Box>
    );
  }

  const first = formatted[0].close;
  const last = formatted.at(-1).close;
  const isUp = last >= first;
  const color = isUp ? "#10b981" : "#ef4444";
  const sym = getCurrencySymbol();


  /* ---------------- CANDLE ---------------- */

  // Smart label generation based on period
  const generateSmartLabels = (formatted, period) => {
    if (!formatted.length) return [];

    const dataWithLabels = [];

    if (period === "1D") {
      // For 1D: Show ONLY time (no dates) every 30 minutes
      const seenTimes = new Set();

      formatted.forEach((d, idx) => {
        const date = new Date(d.ts);
        const hours = date.getHours();
        const mins = date.getMinutes();

        // Create time key for 30-min intervals
        const timeKey = `${hours}:${Math.floor(mins / 30) * 30}`;

        // Show first, last, and every 30 min interval
        const shouldShow =
          idx === 0 ||
          idx === formatted.length - 1 ||
          !seenTimes.has(timeKey);

        let timeLabel = "";
        if (shouldShow) {
          seenTimes.add(timeKey);
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          const displayMins = mins.toString().padStart(2, '0');
          timeLabel = `${displayHours}:${displayMins} ${period}`;
        }

        dataWithLabels.push({ ...d, label: timeLabel });
      });

    } else if (period === "1Y") {
      // For 1Y: Show ONLY month name (no dates, no year) for each month
      const seenMonths = new Set();

      formatted.forEach((d, idx) => {
        const date = new Date(d.ts);
        const month = date.getMonth(); // 0-11
        const year = date.getFullYear();
        const monthKey = `${year}-${month}`;

        let monthLabel = "";
        if (!seenMonths.has(monthKey) || idx === formatted.length - 1) {
          seenMonths.add(monthKey);
          // Just month name: "Jan", "Feb", "Mar", etc.
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          monthLabel = monthNames[month];
        }

        dataWithLabels.push({ ...d, label: monthLabel });
      });

    } else if (period === "6M" || period === "YTD") {
      // For 6M/YTD: Show month names
      const seenMonths = new Set();

      formatted.forEach((d, idx) => {
        const date = new Date(d.ts);
        const month = date.getMonth();
        const year = date.getFullYear();
        const monthKey = `${year}-${month}`;

        let monthLabel = "";
        if (!seenMonths.has(monthKey) || idx === formatted.length - 1) {
          seenMonths.add(monthKey);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          monthLabel = monthNames[month];
        }

        dataWithLabels.push({ ...d, label: monthLabel });
      });

    } else {
      // For 5D, 1M: Show dates
      const showEvery = period === "1M" ? Math.ceil(formatted.length / 6) : Math.ceil(formatted.length / 5);

      formatted.forEach((d, idx) => {
        const shouldShow = idx % showEvery === 0 || idx === formatted.length - 1;
        dataWithLabels.push({
          ...d,
          label: shouldShow ? formatAxis(d.ts, period) : ""
        });
      });
    }

    return dataWithLabels;
  };

  const dataWithLabels = generateSmartLabels(formatted, period);

  // Use categorical x-axis to eliminate gaps for missing datapoints
  const apexOptions = {
    chart: {
      type: "candlestick",
      height: "100%",
      toolbar: { show: false },
      background: "transparent"
    },
    grid: { show: false },
    xaxis: {
      type: "category",
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        showDuplicates: false,
        trim: true,
        minHeight: undefined,
        maxHeight: 120
      },
      tickPlacement: 'between'
    },
    yaxis: [
      {
        seriesName: 'candle',
        show: false,
        tooltip: { enabled: false }
      },
      {
        seriesName: 'volume',
        opposite: true,
        show: false,
        max: function (max) {
          return max * 4; // Make volume take only bottom 25% of chart
        }
      }
    ],
    tooltip: {
      enabled: true,
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const d = dataWithLabels[dataPointIndex];
        return `
          <div style="background: #020617; border: 1px solid #1e293b; color: #e5e7eb; border-radius: 8px; padding: 8px 12px; font-size: 12px;">
            <div style="color: #94a3b8; font-size: 11px; margin-bottom: 4px;">
              ${formatAxis(d.ts, period)}
            </div>
            <div style="display: flex; justify-content: space-between; gap: 16px; margin-top: 4px;">
              <div>
                <div style="color: #94a3b8; font-size: 10px;">Open</div>
                <div style="font-weight: 600;">${sym}${fmtL(d.open, 2)}</div>
              </div>
              <div>
                <div style="color: #94a3b8; font-size: 10px;">High</div>
                <div style="font-weight: 600; color: #10b981;">${sym}${fmtL(d.high, 2)}</div>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; gap: 16px; margin-top: 4px;">
              <div>
                <div style="color: #94a3b8; font-size: 10px;">Low</div>
                <div style="font-weight: 600; color: #ef4444;">${sym}${fmtL(d.low, 2)}</div>
              </div>
              <div>
                <div style="color: #94a3b8; font-size: 10px;">Close</div>
                <div style="font-weight: 600;">${sym}${fmtL(d.close, 2)}</div>
              </div>
            </div>
          </div>
        `;
      }
    },
    plotOptions: {
      candlestick: {
        colors: { upward: "#10b981", downward: "#ef4444" }
      },
      bar: {
        columnWidth: '80%',
        colors: {
          ranges: [{
            from: 0,
            to: Number.MAX_VALUE,
            color: '#64748b'
          }]
        }
      }
    },
    colors: ['#10b981', '#64748b'],
    fill: {
      opacity: [1, 0.2] // Candlestick full opacity, volume 20% opacity
    },
    stroke: {
      width: [1, 0] // Candlestick has stroke, volume doesn't
    },
    dataLabels: {
      enabled: false
    },
    legend: {
      show: false
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          xaxis: { labels: { rotate: -30, style: { fontSize: "10px" } } },
        },
      },
      {
        breakpoint: 480,
        options: {
          xaxis: { labels: { rotate: -30, style: { fontSize: "9px" }, maxHeight: 60 } },
        },
      },
    ],
  };

  const apexSeries = [
    {
      name: 'candle',
      type: 'candlestick',
      data: dataWithLabels.map((d) => ({
        x: d.label,
        y: [d.open, d.high, d.low, d.close]
      }))
    },
    {
      name: 'volume',
      type: 'bar',
      data: dataWithLabels.map((d) => ({
        x: d.label,
        y: d.volume
      }))
    }
  ];

  /* ── Screen-reader data table (3.2.3) ── */
  const srTableId = `chart-data-table-${period || "default"}`;
  const srTable = (
    <table className="sr-only" id={srTableId} aria-label="Chart data table">
      <caption>Price data for {period || "selected"} period</caption>
      <thead>
        <tr><th>Date</th><th>Open</th><th>High</th><th>Low</th><th>Close</th></tr>
      </thead>
      <tbody>
        {formatted.filter((_, i) => i % Math.max(1, Math.floor(formatted.length / 10)) === 0 || i === formatted.length - 1).map((d, i) => (
          <tr key={i}>
            <td>{formatAxis(d.ts, period)}</td>
            <td>{sym}{fmtL(d.open, 2)}</td>
            <td>{sym}{fmtL(d.high, 2)}</td>
            <td>{sym}{fmtL(d.low, 2)}</td>
            <td>{sym}{fmtL(d.close, 2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (chartType === "candlestick") {
    return (
      <>
        <div
          role="img"
          tabIndex={0}
          aria-label={`Candlestick price chart for ${period || "selected"} period. Open: ${sym}${fmtL(formatted[0]?.open, 2)}, Close: ${sym}${fmtL(last, 2)}, High: ${sym}${fmtL(Math.max(...formatted.map(d => d.high)), 2)}, Low: ${sym}${fmtL(Math.min(...formatted.map(d => d.low)), 2)}`}
          aria-describedby={srTableId}
          style={{ width: "100%", height: "100%" }}
        >
          <div aria-hidden="true" style={{ width: "100%", height: "100%" }}>
            <ReactApexChart
              options={apexOptions}
              series={apexSeries}
              type="candlestick"
              height="100%"
              width="100%"
            />
          </div>
        </div>
        {srTable}
      </>
    );
  }

  /* ---------------- LINE ---------------- */

  const values = formatted.map((d) => d.close);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = (max - min) * 0.25 || max * 0.01;

  return (
    <>
      <div role="img" tabIndex={0} aria-label={`Price line chart for ${period || "selected"} period. Start: ${sym}${fmtL(first, 2)}, End: ${sym}${fmtL(last, 2)}, Change: ${isUp ? "+" : ""}${fmtL((last - first) / first * 100, 2)}%`} aria-describedby={srTableId} style={{ width: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={formatted} margin={{ top: 20, right: 60, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                <stop offset="70%" stopColor={color} stopOpacity={0.08} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v) => formatAxis(v, period)}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              minTickGap={period === "1D" ? 40 : 70}
            />

            <YAxis hide domain={[min - padding, max + padding]} />

            <Tooltip content={<CustomTooltip period={period} sym={sym}/>} />

            <Area
              type="natural"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill="url(#fade)"
              dot={false}
            />

            <ReferenceLine
              y={last}
              stroke={color}
              strokeDasharray="3 3"
              label={{
                value: `${sym}${fmtL(last, 2)}`,
                position: "right",
                fill: "#fff",
                fontSize: 12,
                fontWeight: 700
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {srTable}
    </>
  );
}